import { useState, useEffect } from "react";
import { BankFeedsCard } from "./BankFeedsCard";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { usePermissions } from "@/hooks/use-permissions";
import { InlineSectionBoundary } from "@/components/error-boundary";
import { InlineUpgradePanel } from "@/components/inline-upgrade-panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil, Copy, Settings2, Check, Loader2, Landmark, ExternalLink, AlertTriangle } from "lucide-react";
import type { Property, Expense, ExpenseCategorySettings } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { ExpenseWithDoc, EXPENSE_CATEGORIES, MercuryAccountsResponse, formatBalance, MercuryCardStatusInfo, IncomeRow, INCOME_CATEGORIES, DEFAULT_HIGH_CONFIDENCE_SCORE, DEFAULT_AI_SUGGESTION_CONFIDENCE, HIGH_CONFIDENCE_OPTIONS, AI_SUGGESTION_CONFIDENCE_OPTIONS } from "./_shared";

type MercuryConnectionInfo = {
  id: number;
  userId: number;
  token: string;
  status: "active" | "disconnected";
  hasSecretKey: boolean;
  maskedSecretKey: string | null;
  hasApiKey: boolean;
  apiKeyLast4: string | null;
  maskedApiKey: string | null;
  mercuryWebhookId: string | null;
  backfill: {
    status: "idle" | "pending" | "running" | "done" | "failed";
    total: number;
    imported: number;
    skipped: number;
    startDate: string | null;
    startedAt: string | null;
    finishedAt: string | null;
    lastError: string | null;
  };
  eventsReceived: number;
  signatureFailures: number;
  lastEventAt: string | null;
  createdAt: string;
  // Task #2334 — Host-visible warning persisted server-side when the saved
  // Mercury API key didn't return any IO Credit Card accounts. Cleared when
  // a corrected key (or a successful refresh) returns a credit-card account.
  apiKeyScopeWarning: string | null;
};

type MercuryAmbiguous = { mercuryCardId: string; name: string; candidates: { id: number; name: string }[] };

function MercuryCardProvisioningPanel({ readOnly, hasApiKey, properties }: { readOnly: boolean; hasApiKey: boolean; properties: Property[] }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showApiKeyInput, setShowApiKeyInput] = useState(!hasApiKey);
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [template, setTemplate] = useState("PadPulse – {propertyName}");
  const [ambiguous, setAmbiguous] = useState<MercuryAmbiguous[]>([]);
  const [pickedProperty, setPickedProperty] = useState<Record<string, string>>({});

  const statusQuery = useQuery<MercuryCardStatusInfo>({
    queryKey: ["/api/app/mercury/cards/status"],
    retry: false,
  });

  // All mutations must be declared before any early return so the hook call
  // count is stable across renders (Rules of Hooks). The beta-off gate
  // `if (isBetaOff) return null` was previously placed between the useQuery
  // and these useMutation calls, causing a "Rendered fewer hooks than
  // expected" crash whenever the /cards/status route returned 404.
  // Task #2263 — moved all mutations above the early return to fix the crash.
  const saveApiKey = useMutation({
    mutationFn: (apiKey: string) => apiRequest("PUT", "/api/app/mercury-connection/api-key", { apiKey }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-connection"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury/cards/status"] });
      setApiKeyValue(""); setShowApiKeyInput(false);
      toast({ title: "Mercury API token saved" });
    },
    onError: (e: unknown) => toast({ title: "Error", description: parseErrorMessage(e, "Failed to save token"), variant: "destructive" }),
  });
  const syncMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", "/api/app/mercury/cards/sync"),
    onSuccess: async (res: any) => {
      const data = typeof res?.json === "function" ? await res.json() : res;
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury/cards/status"] });
      const amb = (data?.ambiguous ?? []) as MercuryAmbiguous[];
      setAmbiguous(amb);
      toast({
        title: "Sync complete",
        description: `Fetched ${data?.fetched ?? 0}, auto-assigned ${data?.autoAssigned ?? 0}${amb.length ? `, ${amb.length} need manual pick` : ""}.`,
      });
    },
    onError: (e: unknown) => toast({ title: "Sync failed", description: parseErrorMessage(e, "Unknown error"), variant: "destructive" }),
  });
  const provisionMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/app/mercury/cards/provision", { template }),
    onSuccess: async (res: any) => {
      const data = typeof res?.json === "function" ? await res.json() : res;
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury/cards/status"] });
      toast({
        title: "Provisioning complete",
        description: `Created ${data?.succeeded ?? 0} of ${data?.attempted ?? 0}${data?.failed ? `, ${data.failed} failed` : ""}.`,
      });
    },
    onError: (e: unknown) => toast({ title: "Provision failed", description: parseErrorMessage(e, "Unknown error"), variant: "destructive" }),
  });
  const assignMutation = useMutation({
    mutationFn: (vars: { mercuryCardId: string; propertyId: number }) =>
      apiRequest("POST", "/api/app/mercury/cards/assign", vars),
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury/cards/status"] });
      setAmbiguous((cur) => cur.filter((a) => a.mercuryCardId !== vars.mercuryCardId));
      toast({ title: "Card assigned" });
    },
    onError: (e: unknown) => toast({ title: "Assign failed", description: parseErrorMessage(e, "Unknown error"), variant: "destructive" }),
  });

  // Beta flag off → 404 → silently hide the panel. Any other failure (auth,
  // server error, etc.) is surfaced inline with a retry button so the host
  // isn't left wondering why card management vanished.
  const errorMsg = statusQuery.error instanceof Error ? statusQuery.error.message : "";
  const isBetaOff = /^404:/.test(errorMsg);
  if (isBetaOff) return null;
  const status = statusQuery.data;

  return (
    <div className="mt-4 pt-4 border-t border-gray-200" data-testid="mercury-card-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-900">Mercury cards (Beta)</p>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Sync existing Mercury cards into PadPulse and auto-provision new ones for properties that don't have a card yet.
      </p>

      {errorMsg && !isBetaOff && (
        <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-center justify-between" data-testid="text-mercury-status-error">
          <span>Couldn't load Mercury card status: {errorMsg}</span>
          <button
            className="underline text-red-700 hover:text-red-900"
            onClick={() => statusQuery.refetch()}
            data-testid="button-mercury-status-retry"
          >Retry</button>
        </div>
      )}

      {!hasApiKey || showApiKeyInput ? (
        <div className="mt-3">
          <p className="text-xs font-medium text-amber-700 mb-1">
            {hasApiKey ? "Rotate Mercury API token" : "Paste your Mercury API token to enable card management"}
          </p>
          <div className="flex items-center gap-2">
            <Input
              type="password"
              placeholder="Mercury API token (Bearer)"
              value={apiKeyValue}
              onChange={(e) => setApiKeyValue(e.target.value)}
              className="flex-1 text-sm h-8"
              data-testid="input-mercury-api-key"
            />
            <Button
              size="sm" className="h-8"
              onClick={() => saveApiKey.mutate(apiKeyValue)}
              disabled={readOnly || !apiKeyValue.trim() || saveApiKey.isPending}
              data-testid="button-mercury-save-api-key"
            >
              {saveApiKey.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save token"}
            </Button>
            {hasApiKey && (
              <Button size="sm" variant="ghost" className="h-8" onClick={() => { setShowApiKeyInput(false); setApiKeyValue(""); }}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
          <Check className="h-3.5 w-3.5 text-green-500" />
          <span data-testid="text-mercury-api-key-set">API token saved</span>
          <button className="underline text-gray-400 hover:text-gray-600" onClick={() => setShowApiKeyInput(true)} data-testid="button-mercury-rotate-api-key">
            Rotate
          </button>
        </div>
      )}

      {hasApiKey && status && (
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
          <div className="p-2.5 bg-gray-50 rounded border border-gray-200">
            <div className="text-gray-500">Linked cards</div>
            <div className="text-base font-semibold text-gray-900" data-testid="text-mercury-linked-count">{status.linkedCardCount}</div>
          </div>
          <div className="p-2.5 bg-gray-50 rounded border border-gray-200">
            <div className="text-gray-500">Properties missing a card</div>
            <div className="text-base font-semibold text-gray-900" data-testid="text-mercury-missing-count">{status.propertiesMissingCard.length}</div>
          </div>
        </div>
      )}

      {hasApiKey && (
        <div className="mt-3 space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Card name template</label>
            <Input
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              placeholder="PadPulse – {propertyName}"
              className="text-sm h-8"
              data-testid="input-mercury-card-template"
            />
            <p className="text-[10px] text-gray-400 mt-0.5">Use <code>{"{propertyName}"}</code> as a placeholder. Applied to newly provisioned cards.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline"
              onClick={() => syncMutation.mutate()}
              disabled={readOnly || syncMutation.isPending}
              data-testid="button-mercury-sync-cards"
            >
              {syncMutation.isPending ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Syncing…</> : "Sync existing cards"}
            </Button>
            <Button
              size="sm"
              onClick={() => provisionMutation.mutate()}
              disabled={readOnly || provisionMutation.isPending || !status || status.propertiesMissingCard.length === 0}
              data-testid="button-mercury-provision-cards"
            >
              {provisionMutation.isPending ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Provisioning…</> : "Provision missing cards"}
            </Button>
          </div>
        </div>
      )}

      {ambiguous.length > 0 && (
        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded" data-testid="mercury-ambiguous-panel">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-2">{ambiguous.length} card name(s) matched more than one property — pick the right one:</p>
          <div className="space-y-2">
            {ambiguous.map((a) => (
              <div key={a.mercuryCardId} className="flex items-center gap-2 text-xs" data-testid={`mercury-ambiguous-row-${a.mercuryCardId}`}>
                <span className="flex-shrink-0 font-medium text-gray-800 truncate max-w-[200px]" title={a.name}>{a.name}</span>
                <Select
                  value={pickedProperty[a.mercuryCardId] ?? ""}
                  onValueChange={(v) => setPickedProperty((cur) => ({ ...cur, [a.mercuryCardId]: v }))}
                >
                  <SelectTrigger className="h-7 text-xs flex-1" data-testid={`select-mercury-ambiguous-${a.mercuryCardId}`}>
                    <SelectValue placeholder="Pick property…" />
                  </SelectTrigger>
                  <SelectContent>
                    {a.candidates.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm" className="h-7 text-xs"
                  disabled={readOnly || !pickedProperty[a.mercuryCardId] || assignMutation.isPending}
                  onClick={() => assignMutation.mutate({ mercuryCardId: a.mercuryCardId, propertyId: parseInt(pickedProperty[a.mercuryCardId]) })}
                  data-testid={`button-mercury-assign-${a.mercuryCardId}`}
                >Assign</Button>
              </div>
            ))}
          </div>
          {properties.length === 0 && <p className="text-[10px] text-amber-700 mt-1">No properties available.</p>}
        </div>
      )}
    </div>
  );
}

// Task #2049 — outbound IPs that must be whitelisted on the Mercury API token.
// Defined as a constant so they're easy to update in one place.
const MERCURY_WHITELIST_IPS = ["52.5.155.132", "52.87.82.133"] as const;

export function MercuryBankCard({ readOnly, properties: panelProperties = [] }: { readOnly: boolean; properties?: Property[] }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [apiKey, setApiKey] = useState("");
  const [showRotate, setShowRotate] = useState(false);
  // Task #2049 — hide the API key input behind "I have my API key" reveal button.
  // Task #2053 — persist this in sessionStorage so the host doesn't lose their
  // place in the checklist when they open Mercury in another tab to create
  // their token and then come back. Cleared on connect/cancel.
  const [showKeyInput, setShowKeyInputState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem("mercury_connect_step_shown") === "1";
    } catch {
      return false;
    }
  });
  const setShowKeyInput = (value: boolean) => {
    setShowKeyInputState(value);
    if (typeof window === "undefined") return;
    try {
      if (value) {
        window.sessionStorage.setItem("mercury_connect_step_shown", "1");
      } else {
        window.sessionStorage.removeItem("mercury_connect_step_shown");
      }
    } catch {
      /* ignore storage errors (private mode, quota, etc.) */
    }
  };
  // Task #2049 — per-IP copy confirmation ("Copied" flash).
  const [copiedIp, setCopiedIp] = useState<string | null>(null);
  // Task #2049 — flag set when post-connect account seed fails so we show
  // a non-blocking inline notice in the connected state.
  const [accountsFailed, setAccountsFailed] = useState(false);
  // Task #2388 — confirmation state for disconnect when expenses exist.
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  // "all" → no start filter sent to the server (true full history); date string
  // → backfill from that date forward.
  const [startMode, setStartMode] = useState<"date" | "all">("date");
  const [startDate, setStartDate] = useState<string>(() => {
    // Default to 12 months ago — sensible balance between "useful history"
    // and "tens of thousands of transactions on long-running accounts".
    const d = new Date();
    d.setMonth(d.getMonth() - 12);
    return d.toISOString().substring(0, 10);
  });

  const connQuery = useQuery<MercuryConnectionInfo | null>({
    queryKey: ["/api/app/mercury-connection"],
    retry: false,
    // Poll while the backfill is in flight so progress moves on its own.
    refetchInterval: (q) => {
      const status = (q.state.data as MercuryConnectionInfo | null)?.backfill?.status;
      return status === "running" || status === "pending" ? 3000 : false;
    },
  });
  const conn = connQuery.data;
  const isConnected = !!conn && conn.status === "active" && conn.hasApiKey;
  // Task #2388 — count of non-voided Mercury expenses; used to warn before disconnect.
  const expenseCountQuery = useQuery<{ count: number }>({
    queryKey: ["/api/app/mercury-connection/expense-count"],
    enabled: isConnected,
    retry: false,
  });
  const upgradeRequired = connQuery.error instanceof Error && /^403:/.test(connQuery.error.message) && /UPGRADE_REQUIRED/.test(connQuery.error.message);

  // Task #2049 — compact accounts list shown in the connected state.
  // Gated by the mercury_balances beta flag at the route level; a non-200
  // response (e.g. 403 beta-off) is silently swallowed and the section hides.
  // Task #2369 — Settings opts in with `?includeDisabled=true` so accounts
  // the host has hidden from the P&L still show up here with their toggles,
  // even though the P&L tab's MercuryAccountsCard sees the filtered list.
  // A distinct queryKey keeps the two caches independent.
  const accountsQuery = useQuery<MercuryAccountsResponse>({
    queryKey: ["/api/app/mercury/accounts", { includeDisabled: true }],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/app/mercury/accounts?includeDisabled=true");
      return res.json();
    },
    enabled: isConnected,
    retry: false,
  });
  // Clear the accountsFailed notice once the query succeeds.
  useEffect(() => {
    if (accountsQuery.isSuccess && accountsFailed) setAccountsFailed(false);
  }, [accountsQuery.isSuccess, accountsFailed]);

  // Task #2049 — IP copy helper with clipboard API + execCommand fallback.
  function copyIp(ip: string) {
    const doCopy = () => {
      setCopiedIp(ip);
      setTimeout(() => setCopiedIp(null), 2000);
    };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(ip).then(doCopy).catch(() => {
        const el = document.createElement("textarea");
        el.value = ip; el.style.position = "fixed"; el.style.opacity = "0";
        document.body.appendChild(el); el.focus(); el.select();
        try { document.execCommand("copy"); } catch {}
        document.body.removeChild(el);
        doCopy();
      });
    } else {
      const el = document.createElement("textarea");
      el.value = ip; el.style.position = "fixed"; el.style.opacity = "0";
      document.body.appendChild(el); el.focus(); el.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(el);
      doCopy();
    }
  }

  const connectMutation = useMutation({
    // Task #2049 — must parse the response JSON so accountsFailed is accessible.
    mutationFn: async (vars: { apiKey: string; startDate: string | null }) => {
      const res = await apiRequest("POST", "/api/app/mercury-connection", vars);
      return res.json() as Promise<{ accountsFailed?: boolean; [key: string]: unknown }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-connection"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury/accounts"] });
      setApiKey("");
      setShowRotate(false);
      setShowKeyInput(false);
      if (data?.accountsFailed) setAccountsFailed(true);
      toast({ title: "Mercury connected", description: "Importing historical transactions in the background — feel free to keep working." });
    },
    onError: (e: unknown) => {
      toast({ title: "Couldn't connect Mercury", description: parseErrorMessage(e, "Failed to connect"), variant: "destructive" });
    },
  });

  // Task #2049 — wires the "Refresh accounts" / "Retry" buttons in the
  // connected state to the POST refresh endpoint (which re-fetches and
  // upserts balances from Mercury), then invalidates the GET query so the
  // UI reflects the latest data.
  const refreshAccountsMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", "/api/app/mercury/accounts/refresh"),
    onSuccess: () => {
      // Task #2369 — invalidate both the P&L (default) and Settings
      // (includeDisabled) caches so the new balances/accounts show up
      // everywhere immediately.
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury/accounts"] });
      setAccountsFailed(false);
    },
    onError: (e: unknown) => {
      toast({ title: "Couldn't refresh accounts", description: parseErrorMessage(e, "Try again"), variant: "destructive" });
    },
  });

  // Task #2369 — per-account "Include in P&L" toggle. When flipped off, the
  // server hides the account from the P&L tab and excludes its imported
  // expenses/credits from all P&L/ledger queries and reconciliation
  // candidates. Reversible — flipping back on restores everything (no data
  // was deleted).
  const includedInPnlMutation = useMutation({
    mutationFn: ({ mercuryAccountId, included }: { mercuryAccountId: string; included: boolean }) =>
      apiRequest(
        "PATCH",
        `/api/app/mercury/accounts/${encodeURIComponent(mercuryAccountId)}/included-in-pnl`,
        { included },
      ).then((r) => r.json()),
    onMutate: async ({ mercuryAccountId, included }) => {
      await queryClient.cancelQueries({ queryKey: ["/api/app/mercury/accounts"] });
      const settingsKey = ["/api/app/mercury/accounts", { includeDisabled: true }] as const;
      const prevSettings = queryClient.getQueryData<MercuryAccountsResponse>(settingsKey);
      queryClient.setQueryData<MercuryAccountsResponse>(settingsKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          accounts: old.accounts.map((a) =>
            a.mercuryAccountId === mercuryAccountId ? { ...a, includedInPnl: included } : a,
          ),
        };
      });
      return { prevSettings };
    },
    onError: (e: unknown, _vars, ctx) => {
      if (ctx?.prevSettings) {
        queryClient.setQueryData(["/api/app/mercury/accounts", { includeDisabled: true }], ctx.prevSettings);
      }
      toast({ title: "Couldn't update P&L inclusion", description: parseErrorMessage(e, "Try again"), variant: "destructive" });
    },
    onSuccess: (_data, { included }) => {
      // Refresh every consumer that depends on the per-account filter so the
      // P&L tab, expenses tab, income tab and reconciliation panel all reflect
      // the change without a manual reload.
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury/accounts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses/excluded-mercury"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/income"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/expenses/merge-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/profit-and-loss"] });
      toast({
        title: included ? "Account included in P&L" : "Account hidden from P&L",
        description: included
          ? "Balances, totals and transactions for this account are visible in the P&L again."
          : "Existing transactions stay in your records but are excluded from the P&L. Flip back on any time to restore.",
      });
    },
  });

  const backfillMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: (vars: { startDate: string | null }) =>
      apiRequest("POST", "/api/app/mercury-connection/backfill", vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-connection"] });
      toast({ title: "Backfill started", description: "Re-importing transactions from your chosen start date." });
    },
    onError: (e: unknown) => {
      toast({ title: "Couldn't start backfill", description: parseErrorMessage(e, "Failed"), variant: "destructive" });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", "/api/app/mercury-connection"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/mercury-connection"] });
      toast({ title: "Disconnected", description: "Mercury webhook removed on Mercury's side. Existing expenses are preserved." });
    },
    onError: (e: unknown) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Failed to disconnect"), variant: "destructive" });
    },
  });

  function formatLastEvent(dt: string | null) {
    if (!dt) return null;
    const d = new Date(dt);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 2) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  const backfill = conn?.backfill;
  const isBackfilling = backfill?.status === "running" || backfill?.status === "pending";

  return (
    <div className="border border-gray-200 rounded-lg p-6 mt-4" data-testid="mercury-bank-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-950/40 rounded-lg flex items-center justify-center flex-shrink-0">
          <Landmark className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Mercury Bank</h3>
            {isConnected && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800" data-testid="mercury-status-connected">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                Connected
              </span>
            )}
            {conn && !isConnected && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-500" data-testid="mercury-status-disconnected">
                Disconnected
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Paste your Mercury API key once. PadPulse will pull historical transactions, then keep your expenses in sync in real time via webhook.
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Charges on cards assigned to a property auto-attach to that property. Mercury's own category is carried over, and we remember any category change you make per merchant.
          </p>

          {connQuery.isLoading ? (
            <div className="mt-4 space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-3/4" />
            </div>
          ) : upgradeRequired ? (
            // Driven by the server's 403 key, never by hardcoded plan names: the
            // old copy here listed legacy tiers ("PM Pro" included) while the
            // modular engine was denying that very plan.
            <div data-testid="mercury-upgrade-required">
              <InlineUpgradePanel error={connQuery.error} className="mt-4 !p-0 !justify-start" />
            </div>
          ) : !isConnected || showRotate ? (
            // Task #2049 — guided checklist replaces the bare input so hosts
            // know exactly what token name, scope, and IP whitelist to use.
            <div className="mt-4 space-y-4" data-testid="mercury-connect-checklist">
              <p className="text-xs text-gray-500 font-medium">
                {showRotate ? "Follow these steps to create a new Mercury API token:" : "Follow these steps to connect Mercury:"}
              </p>
              <ol className="space-y-3 text-sm text-gray-700" data-testid="mercury-checklist-steps">
                {/* Step 1 */}
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center mt-0.5">1</span>
                  <div>
                    <p className="font-medium text-sm">Open the API tokens page and click <strong>Create token</strong></p>
                    <a
                      href="https://app.mercury.com/settings/tokens"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-1 text-xs text-indigo-600 hover:text-indigo-800 underline"
                      data-testid="link-mercury-tokens-page"
                    >
                      app.mercury.com/settings/tokens <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </li>

                {/* Step 2 */}
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center mt-0.5">2</span>
                  <div>
                    <p className="font-medium text-sm">Name the token <code className="bg-gray-100 px-1 rounded text-indigo-700">PadPulse</code></p>
                    <p className="text-xs text-gray-500 mt-0.5">This name helps identify the token in Mercury's dashboard.</p>
                  </div>
                </li>

                {/* Step 3 */}
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center mt-0.5">3</span>
                  <div>
                    <p className="font-medium text-sm">Select <strong>Read and Write</strong> access</p>
                    <p className="text-xs text-gray-500 mt-0.5">Required for webhook registration and transaction sync.</p>
                  </div>
                </li>

                {/* Step 4 — IP whitelist */}
                <li className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center mt-0.5">4</span>
                  <div className="flex-1">
                    <p className="font-medium text-sm">Add these IPs to the token's IP allowlist</p>
                    <p className="text-xs text-gray-500 mt-0.5">Paste both IPs (comma-separated) into Mercury's IP restriction field.</p>
                    <div className="mt-2" data-testid="mercury-ip-list">
                      <div className="flex items-center gap-2">
                        <code
                          className="flex-1 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs font-mono text-gray-800 select-all"
                          data-testid="text-mercury-ips"
                        >
                          {MERCURY_WHITELIST_IPS.join(", ")}
                        </code>
                        <button
                          type="button"
                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-700 border border-gray-200 rounded px-2 py-1 bg-white hover:bg-indigo-50 transition-colors"
                          onClick={() => copyIp(MERCURY_WHITELIST_IPS.join(", "))}
                          data-testid="button-copy-mercury-ips"
                        >
                          {copiedIp === MERCURY_WHITELIST_IPS.join(", ") ? (
                            <><Check className="h-3 w-3 text-green-600" /><span className="text-green-600">Copied</span></>
                          ) : (
                            <><Copy className="h-3 w-3" />Copy</>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              </ol>

              {/* "I have my API key" reveal */}
              {!showKeyInput ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-1"
                  onClick={() => setShowKeyInput(true)}
                  data-testid="button-mercury-have-api-key"
                >
                  I have my API key
                </Button>
              ) : (
                <div className="space-y-3 pt-1 border-t border-gray-100" data-testid="mercury-api-key-section">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr,220px] gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {showRotate ? "New Mercury API key" : "Mercury API key"}
                      </label>
                      <Input
                        type="password"
                        placeholder="secret-token:..."
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        className="text-sm h-9"
                        data-testid="input-mercury-api-key"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Import from</label>
                      <Select value={startMode} onValueChange={(v: "date" | "all") => setStartMode(v)}>
                        <SelectTrigger className="text-sm h-9" data-testid="select-mercury-start-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="date">Specific date</SelectItem>
                          <SelectItem value="all">All time</SelectItem>
                        </SelectContent>
                      </Select>
                      {startMode === "date" && (
                        <Input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="text-sm h-9 mt-1"
                          data-testid="input-mercury-start-date"
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => connectMutation.mutate({ apiKey: apiKey.trim(), startDate: startMode === "all" ? null : startDate })}
                      disabled={readOnly || connectMutation.isPending || apiKey.trim().length < 8}
                      data-testid="button-mercury-connect"
                    >
                      {connectMutation.isPending ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Connecting...</> : showRotate ? "Save new key" : "Connect Mercury"}
                    </Button>
                    {showRotate && (
                      <Button size="sm" variant="ghost" onClick={() => { setShowRotate(false); setApiKey(""); setShowKeyInput(false); }} data-testid="button-mercury-cancel-rotate">
                        Cancel
                      </Button>
                    )}
                    {!showRotate && (
                      <Button size="sm" variant="ghost" onClick={() => setShowKeyInput(false)} data-testid="button-mercury-hide-key-input">
                        Back
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {/* Task #2334 — Persisted scope warning. Shown inline on the
                  connection card whenever the saved API key didn't return
                  any IO Credit Card accounts (probed at connect time and
                  refreshed on every accounts pull). Re-saving a corrected
                  key clears it server-side. */}
              {conn?.apiKeyScopeWarning && (
                <div
                  className="flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
                  data-testid="notice-mercury-scope-warning"
                >
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium" data-testid="text-mercury-scope-warning-title">
                      API key may be missing IO Credit Card scope
                    </p>
                    <p className="mt-0.5 text-xs" data-testid="text-mercury-scope-warning-body">
                      {conn.apiKeyScopeWarning}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm">
                <span className="text-gray-700">API key</span>
                <code className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-0.5" data-testid="mercury-masked-api-key">
                  {conn?.maskedApiKey ?? "—"}
                </code>
                <button
                  className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                  onClick={() => setShowRotate(true)}
                  disabled={readOnly}
                  data-testid="button-mercury-rotate-key"
                >
                  Rotate API key
                </button>
              </div>

              {/* Backfill progress + re-run */}
              <div className="p-3 rounded-md border border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-medium text-gray-700">Historical import</div>
                  <div className="flex items-center gap-2">
                    {backfill?.status === "done" && (
                      <span className="text-xs text-green-700 inline-flex items-center gap-1" data-testid="mercury-backfill-done">
                        <Check className="h-3 w-3" /> Done
                      </span>
                    )}
                    {isBackfilling && (
                      <span className="text-xs text-indigo-700 inline-flex items-center gap-1" data-testid="mercury-backfill-running">
                        <Loader2 className="h-3 w-3 animate-spin" /> Importing…
                      </span>
                    )}
                    {backfill?.status === "failed" && (
                      <span className="text-xs text-red-700" data-testid="mercury-backfill-failed">Failed</span>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-xs text-gray-500" data-testid="mercury-backfill-counts">
                  {backfill?.imported ?? 0} imported · {backfill?.skipped ?? 0} skipped (credits, transfers, pending)
                  {backfill?.startDate ? ` · since ${backfill.startDate}` : ""}
                </div>
                {backfill?.lastError && (
                  <div className="mt-1 text-xs text-red-600" data-testid="mercury-backfill-error">{backfill.lastError}</div>
                )}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Select value={startMode} onValueChange={(v: "date" | "all") => setStartMode(v)}>
                    <SelectTrigger className="text-xs h-7 w-32" data-testid="select-mercury-rerun-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date">From date</SelectItem>
                      <SelectItem value="all">All time</SelectItem>
                    </SelectContent>
                  </Select>
                  {startMode === "date" && (
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="text-xs h-7 w-40"
                      data-testid="input-mercury-rerun-date"
                    />
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => backfillMutation.mutate({ startDate: startMode === "all" ? null : startDate })}
                    disabled={readOnly || backfillMutation.isPending || isBackfilling}
                    data-testid="button-mercury-rerun-backfill"
                  >
                    {backfillMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Re-run historical import"}
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-1 text-xs text-gray-400 flex-wrap">
                {conn?.lastEventAt ? (
                  <span data-testid="mercury-last-event">Last event: {formatLastEvent(conn.lastEventAt)}</span>
                ) : (
                  <span data-testid="mercury-waiting">Waiting for first live event</span>
                )}
                <span>Events received: {conn?.eventsReceived ?? 0}</span>
                {conn && conn.signatureFailures > 0 && (
                  <span className="text-amber-600">Signature failures: {conn.signatureFailures}</span>
                )}
                <button
                  className="text-red-400 hover:text-red-600 underline ml-auto"
                  onClick={() => setShowDisconnectConfirm(true)}
                  disabled={readOnly || disconnectMutation.isPending}
                  data-testid="button-mercury-disconnect"
                >
                  {disconnectMutation.isPending ? "Disconnecting..." : "Disconnect"}
                </button>
              </div>
              <AlertDialog open={showDisconnectConfirm} onOpenChange={setShowDisconnectConfirm}>
                <AlertDialogContent data-testid="dialog-mercury-disconnect-confirm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Disconnect Mercury Bank?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {(expenseCountQuery.data?.count ?? 0) > 0 ? (
                        <>
                          You have <strong>{expenseCountQuery.data!.count} Mercury transaction{expenseCountQuery.data!.count === 1 ? "" : "s"}</strong> already imported. These expenses will be retained in your records — nothing is deleted. The account link will be cleared and no new transactions will sync until you reconnect.
                        </>
                      ) : (
                        "This will remove the Mercury webhook. No expenses will be deleted. You can reconnect any time."
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="button-mercury-disconnect-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => { setShowDisconnectConfirm(false); disconnectMutation.mutate(); }}
                      data-testid="button-mercury-disconnect-confirm"
                    >
                      Disconnect
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Task #2049 — compact accounts list with balances */}
              {(() => {
                const accts = accountsQuery.data?.accounts ?? [];
                const acctError = accountsQuery.error instanceof Error ? accountsQuery.error.message : "";
                const betaOff = /^403:|^404:/.test(acctError);
                if (betaOff) return null;

                return (
                  <div className="pt-2" data-testid="mercury-accounts-section">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-xs font-medium text-gray-700">Connected accounts</p>
                      {accts.length > 0 && (
                        <button
                          className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                          onClick={() => refreshAccountsMutation.mutate()}
                          disabled={refreshAccountsMutation.isPending}
                          data-testid="button-mercury-refresh-accounts"
                        >
                          {refreshAccountsMutation.isPending ? "Refreshing…" : "Refresh"}
                        </button>
                      )}
                    </div>

                    {accountsFailed && accts.length === 0 && (
                      <div className="flex items-center gap-2 p-2.5 rounded bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200" data-testid="mercury-accounts-failed-notice">
                        <span className="flex-1">We couldn't load your accounts right now — try refreshing.</span>
                        <button
                          className="underline text-amber-800 dark:text-amber-200 hover:text-amber-900 dark:hover:text-amber-100 whitespace-nowrap"
                          onClick={() => refreshAccountsMutation.mutate()}
                          disabled={refreshAccountsMutation.isPending}
                          data-testid="button-mercury-retry-accounts"
                        >
                          {refreshAccountsMutation.isPending ? "Refreshing…" : "Refresh accounts"}
                        </button>
                      </div>
                    )}

                    {refreshAccountsMutation.isPending && accts.length === 0 && (
                      <div className="space-y-1.5">
                        <Skeleton className="h-9 w-full rounded" />
                        <Skeleton className="h-9 w-full rounded" />
                      </div>
                    )}

                    {accts.length > 0 && (
                      <p className="mb-1.5 text-[11px] text-gray-500" data-testid="text-mercury-included-in-pnl-helper">
                        Hidden accounts don't appear on the P&amp;L and stop importing new transactions. Flip back on any time to restore.
                      </p>
                    )}

                    {accts.length > 0 && (
                      <div className="divide-y divide-gray-100 rounded border border-gray-200 bg-gray-50 overflow-hidden" data-testid="mercury-accounts-list">
                        {accts.map((acct) => {
                          const label = acct.nickname ?? acct.mercuryAccountId;
                          const balance = formatBalance(acct.currentBalance, acct.currency ?? "USD");
                          // Task #2369 — per-account "Include in P&L" toggle.
                          // When off, the entire account (balances, totals,
                          // monthly columns, imported expenses/credits) is
                          // hidden from the P&L tab.
                          const includedInPnl = acct.includedInPnl !== false;
                          return (
                            <div key={acct.id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid={`mercury-account-row-${acct.id}`}>
                              <div className="min-w-0 flex-1">
                                <span className={`font-medium ${includedInPnl ? "text-gray-800" : "text-gray-500"}`}>{label}</span>
                                {acct.kind && <span className="ml-1.5 text-gray-400 capitalize">{acct.kind}</span>}
                                {!includedInPnl && (
                                  <span className="ml-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 uppercase tracking-wide" data-testid={`badge-mercury-excluded-${acct.mercuryAccountId}`}>
                                    Hidden from P&amp;L
                                  </span>
                                )}
                              </div>
                              <span className={`font-semibold tabular-nums ${includedInPnl ? "text-gray-900" : "text-gray-400"}`} data-testid={`mercury-account-balance-${acct.id}`}>{balance}</span>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-[11px] text-gray-500 whitespace-nowrap">In P&amp;L</span>
                                <Switch
                                  checked={includedInPnl}
                                  disabled={readOnly || includedInPnlMutation.isPending}
                                  onCheckedChange={(checked) =>
                                    includedInPnlMutation.mutate({ mercuryAccountId: acct.mercuryAccountId, included: checked })
                                  }
                                  data-testid={`switch-mercury-included-in-pnl-${acct.mercuryAccountId}`}
                                  aria-label={includedInPnl ? "Hide this account from the P&L" : "Include this account in the P&L"}
                                  title={includedInPnl ? "Counted in P&L — click to hide" : "Hidden from P&L — click to include"}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {acctError && !betaOff && !accountsFailed && (
                      <div className="flex items-center gap-2 p-2.5 rounded bg-red-50 border border-red-200 text-xs text-red-700" data-testid="mercury-accounts-error">
                        <span className="flex-1">Couldn't load accounts: {acctError}</span>
                        <button className="underline hover:text-red-900" onClick={() => refreshAccountsMutation.mutate()} disabled={refreshAccountsMutation.isPending} data-testid="button-mercury-accounts-retry">
                          Retry
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {conn.hasSecretKey && (
                <MercuryCardProvisioningPanel readOnly={readOnly} hasApiKey={!!conn.hasApiKey} properties={panelProperties} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export function SettingsTab({ properties = [] }: { properties?: Property[] }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit: canEditArea } = usePermissions();
  const readOnly = !canEditArea("financials");
  const [showAddCatForm, setShowAddCatForm] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatType, setNewCatType] = useState<"income" | "expense" | "balance_sheet">("expense");
  // Task #2335 — Track usage count so the inline delete confirm can warn the
  // host how many P&L rows reference this category. `count === null` means
  // the count is still being computed (we haven't opened the confirm yet).
  const [deleteConfirm, setDeleteConfirm] = useState<{ value: string; source: "expense" | "income"; count: number } | null>(null);
  // Task #2330 — Inline rename of custom categories
  const [editingCat, setEditingCat] = useState<{ value: string; source: "expense" | "income" } | null>(null);
  const [editingName, setEditingName] = useState("");
  // Task #2333 — Confirm rename when the category is already referenced by
  // existing transactions so the host knows the new label will retroactively
  // re-label historical P&L rows.
  const [renameConfirm, setRenameConfirm] = useState<
    | { value: string; source: "expense" | "income"; name: string; count: number }
    | null
  >(null);

  // Task #2333 — Reuse the same expenses/income endpoints the P&L tab uses
  // so we can count how many transactions reference a category without
  // adding a dedicated count endpoint. React Query dedupes these calls
  // across components when both tabs have been visited.
  const expensesUsageQuery = useQuery<ExpenseWithDoc[]>({ queryKey: ["/api/app/expenses"] });
  const incomeUsageQuery = useQuery<IncomeRow[]>({
    queryKey: ["/api/app/income"],
    queryFn: async () => {
      const res = await fetch("/api/app/income");
      if (!res.ok) throw new Error("Failed to load income");
      return res.json();
    },
  });

  const catSettingsQuery = useQuery<ExpenseCategorySettings>({ queryKey: ["/api/app/expense-category-settings"] });
  const settings = catSettingsQuery.data;
  const disabledCategories = (settings?.disabledCategories as string[]) || [];
  const customCategories = (settings?.customCategories as {label: string, value: string, isBalanceSheet?: boolean}[]) || [];
  const mercuryThreshold = settings?.mercuryHighConfidenceThreshold ?? DEFAULT_HIGH_CONFIDENCE_SCORE;
  const mercuryAutoMergeEnabled = settings?.mercuryAutoMergeEnabled ?? false;
  // Task #2166 — opt-in auto-apply of high-confidence AI category suggestions
  // on Mercury import.
  const aiAutoCategorizeEnabled = (settings as any)?.aiAutoCategorizeEnabled ?? false;
  const aiAutoCategorizeThreshold = (settings as any)?.aiAutoCategorizeThreshold ?? 95;

  const aiAutoCategorizeMutation = useMutation({
    mutationFn: async (value: boolean) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("PUT", "/api/app/expense-category-settings", { aiAutoCategorizeEnabled: value });
    },
    onSuccess: (_data, value) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      toast({
        title: value ? "AI auto-categorize turned on" : "AI auto-categorize turned off",
        description: value
          ? "New Mercury imports above your confidence threshold will be categorized automatically."
          : "New Mercury imports will land in Uncategorized for you to review.",
      });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Failed to update AI auto-categorize setting";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  const aiAutoCategorizeThresholdMutation = useMutation({
    mutationFn: async (value: number) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("PUT", "/api/app/expense-category-settings", { aiAutoCategorizeThreshold: value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      toast({ title: "AI auto-categorize threshold updated" });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Failed to update threshold";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  const autoMergeMutation = useMutation({
    mutationFn: async (value: boolean) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("PUT", "/api/app/expense-category-settings", { mercuryAutoMergeEnabled: value });
    },
    onSuccess: (_data, value) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      toast({
        title: value ? "Auto-merge turned on" : "Auto-merge turned off",
        description: value
          ? "New high-confidence Mercury matches will merge automatically."
          : "New Mercury matches will wait for you to merge them.",
      });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Failed to update auto-merge setting";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  const thresholdMutation = useMutation({
    mutationFn: async (value: number) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("PUT", "/api/app/expense-category-settings", { mercuryHighConfidenceThreshold: value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      toast({ title: "High-confidence threshold updated" });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Failed to update threshold";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  const aiThresholdMutation = useMutation({
    mutationFn: async (value: number) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("PUT", "/api/app/expense-category-settings", { aiSuggestionConfidenceThreshold: value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      toast({ title: "AI suggestion threshold updated" });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Failed to update threshold";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
  const aiSuggestionThreshold =
    settings?.aiSuggestionConfidenceThreshold ?? DEFAULT_AI_SUGGESTION_CONFIDENCE;

  const toggleMutation = useMutation({
    mutationFn: async (data: { disabledCategories: string[] }) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("PUT", "/api/app/expense-category-settings", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Failed to update category";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  const addCustomMutation = useMutation({
    mutationFn: async (data: { name: string; isBalanceSheet: boolean }) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("POST", "/api/app/expense-category-settings/custom", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      setNewCatName("");
      setShowAddCatForm(false);
      toast({ title: "Category added" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: parseErrorMessage(err, "Failed to add category"), variant: "destructive" });
    },
  });

  const deleteCustomMutation = useMutation({
    mutationFn: async (value: string) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("DELETE", `/api/app/expense-category-settings/custom/${encodeURIComponent(value)}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      setDeleteConfirm(null);
      toast({ title: "Category removed" });
    },
  });

  const customIncomeCategories = ((settings as any)?.customIncomeCategories as {label: string, value: string}[]) || [];

  const addCustomIncomeMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("POST", "/api/app/income-category-settings/custom", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      setNewCatName("");
      setShowAddCatForm(false);
      toast({ title: "Category added" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: parseErrorMessage(err, "Failed to add income category"), variant: "destructive" });
    },
  });

  const deleteCustomIncomeMutation = useMutation({
    mutationFn: async (value: string) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("DELETE", `/api/app/income-category-settings/custom/${encodeURIComponent(value)}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      setDeleteConfirm(null);
      toast({ title: "Category removed" });
    },
  });

  // Task #2330 — rename custom income/expense categories
  const renameCustomIncomeMutation = useMutation({
    mutationFn: async ({ value, name }: { value: string; name: string }) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("PUT", `/api/app/income-category-settings/custom/${encodeURIComponent(value)}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      setEditingCat(null);
      setEditingName("");
      toast({ title: "Category renamed" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: parseErrorMessage(err, "Failed to rename category"), variant: "destructive" });
    },
  });

  const renameCustomExpenseMutation = useMutation({
    mutationFn: async ({ value, name }: { value: string; name: string }) => {
      if (readOnly) throw new Error("You have view-only access to financials.");
      return apiRequest("PUT", `/api/app/expense-category-settings/custom/${encodeURIComponent(value)}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/expense-category-settings"] });
      setEditingCat(null);
      setEditingName("");
      toast({ title: "Category renamed" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: parseErrorMessage(err, "Failed to rename category"), variant: "destructive" });
    },
  });

  function startEdit(value: string, source: "expense" | "income", label: string) {
    setEditingCat({ value, source });
    setEditingName(label);
    setDeleteConfirm(null);
  }
  function cancelEdit() {
    setEditingCat(null);
    setEditingName("");
  }
  function performRename(value: string, source: "expense" | "income", name: string) {
    if (source === "income") {
      renameCustomIncomeMutation.mutate({ value, name });
    } else {
      renameCustomExpenseMutation.mutate({ value, name });
    }
  }

  async function saveEdit() {
    if (!editingCat) return;
    const name = editingName.trim();
    if (!name) return;
    // Task #2333 — Count transaction usage of this category before renaming.
    // Expense-source custom categories live on `expenses.category`; income-source
    // ones live on `income_transactions.category`. We must NOT silently fall back
    // to zero when the usage query is still loading or has errored, otherwise the
    // warning gets bypassed in real-world timing/network conditions.
    const editing = editingCat;
    try {
      let count = 0;
      if (editing.source === "income") {
        const result = await incomeUsageQuery.refetch();
        if (result.error || !result.data) {
          throw result.error ?? new Error("Could not load income transactions");
        }
        count = result.data.filter((r) => r.category === editing.value).length;
      } else {
        const result = await expensesUsageQuery.refetch();
        if (result.error || !result.data) {
          throw result.error ?? new Error("Could not load expenses");
        }
        count = result.data.filter(
          (e) => e.category === editing.value && !e.voidedAt && !e.mergedIntoExpenseId,
        ).length;
      }
      if (count > 0) {
        setRenameConfirm({ value: editing.value, source: editing.source, name, count });
        return;
      }
      performRename(editing.value, editing.source, name);
    } catch (err) {
      toast({
        title: "Couldn't check category usage",
        description:
          "We couldn't load your transactions to confirm how many use this category. Please try again.",
        variant: "destructive",
      });
    }
  }

  // Task #2335 — Before opening the inline delete confirm, count how many
  // expense/income transactions still reference this custom category so the
  // host knows their P&L rows will start showing the raw value (e.g.
  // `other_income`) once the category label is removed. Mirrors the rename
  // usage-count flow added in Task #2333.
  async function startDelete(value: string, source: "expense" | "income") {
    try {
      let count = 0;
      if (source === "income") {
        const result = await incomeUsageQuery.refetch();
        if (result.error || !result.data) {
          throw result.error ?? new Error("Could not load income transactions");
        }
        count = result.data.filter((r) => r.category === value).length;
      } else {
        const result = await expensesUsageQuery.refetch();
        if (result.error || !result.data) {
          throw result.error ?? new Error("Could not load expenses");
        }
        count = result.data.filter(
          (e) => e.category === value && !e.voidedAt && !e.mergedIntoExpenseId,
        ).length;
      }
      setDeleteConfirm({ value, source, count });
    } catch (err) {
      toast({
        title: "Couldn't check category usage",
        description:
          "We couldn't load your transactions to confirm how many use this category. Please try again.",
        variant: "destructive",
      });
    }
  }

  function handleToggle(categoryValue: string, enabled: boolean) {
    const updated = enabled
      ? disabledCategories.filter((c) => c !== categoryValue)
      : [...disabledCategories, categoryValue];
    toggleMutation.mutate({ disabledCategories: updated });
  }

  function handleToggleIncome(categoryValue: string, enabled: boolean) {
    const curr = ((settings as any)?.disabledIncomeCategories as string[]) || [];
    const updated = enabled ? curr.filter((c) => c !== categoryValue) : [...curr, categoryValue];
    toggleMutation.mutate({ disabledIncomeCategories: updated } as any);
  }

  function handleAddUnifiedCategory() {
    if (!newCatName.trim()) return;
    if (newCatType === "income") {
      addCustomIncomeMutation.mutate({ name: newCatName.trim() });
    } else {
      addCustomMutation.mutate({ name: newCatName.trim(), isBalanceSheet: newCatType === "balance_sheet" });
    }
  }

  return (
    <>
    <div className="max-w-2xl">
      <InlineSectionBoundary>
        <BankFeedsCard properties={properties} />
      </InlineSectionBoundary>
    </div>
    <fieldset disabled={readOnly} style={{ display: "contents" }}>
    <div data-testid="settings-tab">
      <div className="max-w-2xl">
        <InlineSectionBoundary>
          <MercuryBankCard readOnly={readOnly} properties={properties} />
        </InlineSectionBoundary>

        <div className="border border-gray-200 rounded-lg p-6 mt-4" data-testid="card-mercury-threshold">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-950/40 rounded-lg flex items-center justify-center flex-shrink-0">
              <Settings2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">Mercury reconciliation</h3>
              <p className="text-sm text-gray-500 mt-1">
                Choose the match score at which a Mercury suggestion counts as "high confidence."
                The <strong>Select all high-confidence</strong> checkbox on the reconciliation panel
                uses this cutoff. Lower it to bulk-merge more aggressively, raise it to keep tighter
                control. Default is 85.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-700" htmlFor="select-mercury-threshold">
              High-confidence threshold
            </label>
            <Select
              value={String(mercuryThreshold)}
              onValueChange={(v) => thresholdMutation.mutate(Number(v))}
              disabled={readOnly || catSettingsQuery.isLoading || thresholdMutation.isPending}
            >
              <SelectTrigger
                id="select-mercury-threshold"
                className="w-32"
                data-testid="select-mercury-threshold"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HIGH_CONFIDENCE_OPTIONS.map((v) => (
                  <SelectItem
                    key={v}
                    value={String(v)}
                    data-testid={`option-mercury-threshold-${v}`}
                  >
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-start justify-between gap-4 mt-5 pt-5 border-t border-gray-100" data-testid="row-mercury-auto-merge">
            <div className="flex-1">
              <label htmlFor="toggle-mercury-auto-merge" className="text-sm font-medium text-gray-900">
                Auto-merge high-confidence matches
              </label>
              <p className="text-xs text-gray-500 mt-1">
                When on, PadPulse merges any new Mercury suggestion whose score meets or beats your threshold
                ({mercuryThreshold}) the moment it lands. Auto-merged rows still appear under <strong>Recently merged</strong> so you can unmerge them if needed,
                and we'll post an in-app notification each time a batch is merged.
              </p>
            </div>
            <Switch
              id="toggle-mercury-auto-merge"
              checked={mercuryAutoMergeEnabled}
              onCheckedChange={(checked) => autoMergeMutation.mutate(checked)}
              disabled={readOnly || catSettingsQuery.isLoading || autoMergeMutation.isPending}
              data-testid="toggle-mercury-auto-merge"
            />
          </div>
          {/* Task #2166 — AI auto-categorize toggle + threshold */}
          <div className="flex items-start justify-between gap-4 mt-5 pt-5 border-t border-gray-100" data-testid="row-ai-auto-categorize">
            <div className="flex-1">
              <label htmlFor="toggle-ai-auto-categorize" className="text-sm font-medium text-gray-900">
                Auto-apply high-confidence AI category suggestions
              </label>
              <p className="text-xs text-gray-500 mt-1">
                When on, brand-new Mercury imports landing in <strong>Uncategorized</strong> are run through PadPulse's AI categorizer at import time.
                If the AI is at least <strong>{aiAutoCategorizeThreshold}%</strong> confident, the category is applied automatically and the row is tagged
                so you can audit or bulk-revert. Existing categorized rows are never touched.
              </p>
              <div className="flex items-center gap-3 mt-3">
                <label className="text-xs text-gray-600" htmlFor="select-ai-auto-threshold">
                  Confidence threshold
                </label>
                <Select
                  value={String(aiAutoCategorizeThreshold)}
                  onValueChange={(v) => aiAutoCategorizeThresholdMutation.mutate(Number(v))}
                  disabled={readOnly || !aiAutoCategorizeEnabled || catSettingsQuery.isLoading || aiAutoCategorizeThresholdMutation.isPending}
                >
                  <SelectTrigger
                    id="select-ai-auto-threshold"
                    className="w-32"
                    data-testid="select-ai-auto-threshold"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[80, 85, 90, 95, 99].map((v) => (
                      <SelectItem
                        key={v}
                        value={String(v)}
                        data-testid={`option-ai-auto-threshold-${v}`}
                      >
                        {v}%
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Switch
              id="toggle-ai-auto-categorize"
              checked={aiAutoCategorizeEnabled}
              onCheckedChange={(checked) => aiAutoCategorizeMutation.mutate(checked)}
              disabled={readOnly || catSettingsQuery.isLoading || aiAutoCategorizeMutation.isPending}
              data-testid="toggle-ai-auto-categorize"
            />
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-6 mt-4" data-testid="card-ai-suggestion-threshold">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-violet-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <Settings2 className="h-6 w-6 text-violet-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">AI category suggestions</h3>
              <p className="text-sm text-gray-500 mt-1">
                Controls the confidence cutoff for the violet <strong>AI:</strong> pill on uncategorized
                expenses. Lower it to see noisier suggestions for faster cleanup, raise it to keep only
                near-perfect picks. Default is 85.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-700" htmlFor="select-ai-suggestion-threshold">
              Suggestion confidence threshold
            </label>
            <Select
              value={String(aiSuggestionThreshold)}
              onValueChange={(v) => aiThresholdMutation.mutate(Number(v))}
              disabled={readOnly || catSettingsQuery.isLoading || aiThresholdMutation.isPending}
            >
              <SelectTrigger
                id="select-ai-suggestion-threshold"
                className="w-32"
                data-testid="select-ai-suggestion-threshold"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AI_SUGGESTION_CONFIDENCE_OPTIONS.map((v) => (
                  <SelectItem
                    key={v}
                    value={String(v)}
                    data-testid={`option-ai-suggestion-threshold-${v}`}
                  >
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Task #2306 — Unified Categories table (replaces separate Income/Expense/Custom sections) */}
        <div className="border border-gray-200 rounded-lg p-6 mt-4" data-testid="card-categories">
          <div className="flex items-center justify-between mb-4 gap-4">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-950/40 rounded-lg flex items-center justify-center flex-shrink-0">
                <Settings2 className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-900">Categories</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Manage income, expense, and balance sheet categories. Toggle built-in categories on or off, or add your own.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setShowAddCatForm((v) => !v); setNewCatName(""); setNewCatType("expense"); }}
              disabled={readOnly}
              data-testid="button-add-category"
              className="flex-shrink-0"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Category
            </Button>
          </div>

          {showAddCatForm && (
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700" data-testid="form-add-category">
              <p className="text-sm font-medium text-gray-700 mb-3">New category</p>
              <div className="flex gap-2 flex-wrap">
                <Input
                  placeholder="Category name"
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddUnifiedCategory()}
                  className="flex-1 min-w-40"
                  autoFocus
                  data-testid="input-new-category-name"
                />
                <Select value={newCatType} onValueChange={(v) => setNewCatType(v as "income" | "expense" | "balance_sheet")}>
                  <SelectTrigger className="w-44" data-testid="select-new-category-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="balance_sheet">Balance Sheet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 justify-end mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowAddCatForm(false); setNewCatName(""); }}
                  data-testid="button-cancel-add-category"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddUnifiedCategory}
                  disabled={!newCatName.trim() || addCustomMutation.isPending || addCustomIncomeMutation.isPending}
                  data-testid="button-confirm-add-category"
                >
                  {addCustomMutation.isPending || addCustomIncomeMutation.isPending ? "Adding..." : "Add Category"}
                </Button>
              </div>
            </div>
          )}

          {catSettingsQuery.isLoading ? (
            <div className="space-y-2">{[1,2,3,4,5].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <div className="rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-sm" data-testid="table-categories">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2.5 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Name</th>
                    <th className="text-left py-2.5 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide w-36">Type</th>
                    <th className="py-2.5 px-4 w-24 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {/* Built-in income categories with enable toggle (except Other Income which is pinned) */}
                  {INCOME_CATEGORIES.filter((c) => c.value !== "other_income").map((cat) => {
                    const disabledIncomeCats = ((settings as any)?.disabledIncomeCategories as string[]) || [];
                    const isEnabled = !disabledIncomeCats.includes(cat.value);
                    return (
                      <tr key={`bi-inc-${cat.value}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40" data-testid={`category-row-income-${cat.value}`}>
                        <td className="py-2.5 px-4 text-gray-700 dark:text-gray-300">{cat.label}</td>
                        <td className="py-2.5 px-4">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">Income</span>
                        </td>
                        <td className="py-2.5 px-4 text-right">
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={(checked) => handleToggleIncome(cat.value, checked)}
                            data-testid={`toggle-income-${cat.value}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {/* Built-in expense categories with enable toggle (except Other and CapEx which are pinned) */}
                  {EXPENSE_CATEGORIES.filter((c) => c.value !== "other" && c.value !== "capex").map((cat) => {
                    const isEnabled = !disabledCategories.includes(cat.value);
                    return (
                      <tr key={`bi-exp-${cat.value}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40" data-testid={`category-row-expense-${cat.value}`}>
                        <td className="py-2.5 px-4 text-gray-700 dark:text-gray-300">{cat.label}</td>
                        <td className="py-2.5 px-4">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400">Expense</span>
                        </td>
                        <td className="py-2.5 px-4 text-right">
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={(checked) => handleToggle(cat.value, checked)}
                            data-testid={`toggle-expense-${cat.value}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {/* Custom income categories */}
                  {customIncomeCategories.map((cat) => {
                    const isEditing = editingCat?.value === cat.value && editingCat?.source === "income";
                    return (
                    <tr key={`ci-${cat.value}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40" data-testid={`category-row-custom-income-${cat.value}`}>
                      <td className="py-2.5 px-4 text-gray-700 dark:text-gray-300">
                        {isEditing ? (
                          <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                            className="h-8 max-w-xs"
                            autoFocus
                            data-testid={`input-rename-income-${cat.value}`}
                          />
                        ) : cat.label}
                      </td>
                      <td className="py-2.5 px-4">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">Income</span>
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        {isEditing ? (
                          <span className="inline-flex items-center gap-1">
                            <Button size="sm" className="h-7 text-xs" onClick={saveEdit} disabled={!editingName.trim() || renameCustomIncomeMutation.isPending} data-testid={`button-save-rename-income-${cat.value}`}>Save</Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={cancelEdit} data-testid={`button-cancel-rename-income-${cat.value}`}>Cancel</Button>
                          </span>
                        ) : deleteConfirm?.value === cat.value && deleteConfirm?.source === "income" ? (
                          <span className="inline-flex items-center gap-1">
                            <span className="text-xs text-red-600 mr-1" data-testid={`text-delete-confirm-income-${cat.value}`}>
                              {deleteConfirm.count > 0
                                ? `This category is used on ${deleteConfirm.count} ${deleteConfirm.count === 1 ? "transaction" : "transactions"} — delete anyway?`
                                : "Delete?"}
                            </span>
                            <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => deleteCustomIncomeMutation.mutate(cat.value)} disabled={deleteCustomIncomeMutation.isPending} data-testid={`button-confirm-delete-income-${cat.value}`}>Yes</Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setDeleteConfirm(null)} data-testid={`button-cancel-delete-income-${cat.value}`}>No</Button>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5">
                            <Button variant="ghost" size="sm" className="h-7 text-gray-400 hover:text-blue-600" onClick={() => startEdit(cat.value, "income", cat.label)} data-testid={`button-rename-income-${cat.value}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 text-gray-400 hover:text-red-600" onClick={() => startDelete(cat.value, "income")} data-testid={`button-delete-income-${cat.value}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                  {/* Custom expense categories (non-balance-sheet) */}
                  {customCategories.filter((c) => !c.isBalanceSheet).map((cat) => {
                    const isEditing = editingCat?.value === cat.value && editingCat?.source === "expense";
                    return (
                    <tr key={`ce-${cat.value}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40" data-testid={`category-row-custom-expense-${cat.value}`}>
                      <td className="py-2.5 px-4 text-gray-700 dark:text-gray-300">
                        {isEditing ? (
                          <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                            className="h-8 max-w-xs"
                            autoFocus
                            data-testid={`input-rename-expense-${cat.value}`}
                          />
                        ) : cat.label}
                      </td>
                      <td className="py-2.5 px-4">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400">Expense</span>
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        {isEditing ? (
                          <span className="inline-flex items-center gap-1">
                            <Button size="sm" className="h-7 text-xs" onClick={saveEdit} disabled={!editingName.trim() || renameCustomExpenseMutation.isPending} data-testid={`button-save-rename-${cat.value}`}>Save</Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={cancelEdit} data-testid={`button-cancel-rename-${cat.value}`}>Cancel</Button>
                          </span>
                        ) : deleteConfirm?.value === cat.value && deleteConfirm?.source === "expense" ? (
                          <span className="inline-flex items-center gap-1">
                            <span className="text-xs text-red-600 mr-1" data-testid={`text-delete-confirm-${cat.value}`}>
                              {deleteConfirm.count > 0
                                ? `This category is used on ${deleteConfirm.count} ${deleteConfirm.count === 1 ? "transaction" : "transactions"} — delete anyway?`
                                : "Delete?"}
                            </span>
                            <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => deleteCustomMutation.mutate(cat.value)} disabled={deleteCustomMutation.isPending} data-testid={`button-confirm-delete-${cat.value}`}>Yes</Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setDeleteConfirm(null)} data-testid={`button-cancel-delete-${cat.value}`}>No</Button>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5">
                            <Button variant="ghost" size="sm" className="h-7 text-gray-400 hover:text-blue-600" onClick={() => startEdit(cat.value, "expense", cat.label)} data-testid={`button-rename-${cat.value}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 text-gray-400 hover:text-red-600" onClick={() => startDelete(cat.value, "expense")} data-testid={`button-delete-${cat.value}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                  {/* Custom balance sheet categories */}
                  {customCategories.filter((c) => c.isBalanceSheet).map((cat) => {
                    const isEditing = editingCat?.value === cat.value && editingCat?.source === "expense";
                    return (
                    <tr key={`cbs-${cat.value}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/40" data-testid={`category-row-custom-bs-${cat.value}`}>
                      <td className="py-2.5 px-4 text-gray-700 dark:text-gray-300">
                        {isEditing ? (
                          <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                            className="h-8 max-w-xs"
                            autoFocus
                            data-testid={`input-rename-bs-${cat.value}`}
                          />
                        ) : cat.label}
                      </td>
                      <td className="py-2.5 px-4">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400">Balance Sheet</span>
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        {isEditing ? (
                          <span className="inline-flex items-center gap-1">
                            <Button size="sm" className="h-7 text-xs" onClick={saveEdit} disabled={!editingName.trim() || renameCustomExpenseMutation.isPending} data-testid={`button-save-rename-bs-${cat.value}`}>Save</Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={cancelEdit} data-testid={`button-cancel-rename-bs-${cat.value}`}>Cancel</Button>
                          </span>
                        ) : deleteConfirm?.value === cat.value && deleteConfirm?.source === "expense" ? (
                          <span className="inline-flex items-center gap-1">
                            <span className="text-xs text-red-600 mr-1" data-testid={`text-delete-confirm-bs-${cat.value}`}>
                              {deleteConfirm.count > 0
                                ? `This category is used on ${deleteConfirm.count} ${deleteConfirm.count === 1 ? "transaction" : "transactions"} — delete anyway?`
                                : "Delete?"}
                            </span>
                            <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => deleteCustomMutation.mutate(cat.value)} disabled={deleteCustomMutation.isPending} data-testid={`button-confirm-delete-bs-${cat.value}`}>Yes</Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setDeleteConfirm(null)} data-testid={`button-cancel-delete-bs-${cat.value}`}>No</Button>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5">
                            <Button variant="ghost" size="sm" className="h-7 text-gray-400 hover:text-blue-600" onClick={() => startEdit(cat.value, "expense", cat.label)} data-testid={`button-rename-bs-${cat.value}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 text-gray-400 hover:text-red-600" onClick={() => startDelete(cat.value, "expense")} data-testid={`button-delete-bs-${cat.value}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                  {/* Pinned catch-all rows — always on, cannot be deleted or toggled */}
                  <tr className="bg-gray-50/60 dark:bg-gray-800/20" data-testid="category-row-pinned-other-income">
                    <td className="py-2.5 px-4 text-gray-600 dark:text-gray-400 italic">Other Income</td>
                    <td className="py-2.5 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">Income</span>
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <span className="text-xs text-gray-400">Always on</span>
                    </td>
                  </tr>
                  <tr className="bg-gray-50/60 dark:bg-gray-800/20" data-testid="category-row-pinned-other-expenses">
                    <td className="py-2.5 px-4 text-gray-600 dark:text-gray-400 italic">Other Expenses</td>
                    <td className="py-2.5 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400">Expense</span>
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <span className="text-xs text-gray-400">Always on</span>
                    </td>
                  </tr>
                  <tr className="bg-gray-50/60 dark:bg-gray-800/20" data-testid="category-row-pinned-capex">
                    <td className="py-2.5 px-4 text-gray-600 dark:text-gray-400 italic">CapEx</td>
                    <td className="py-2.5 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400">Balance Sheet</span>
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <span className="text-xs text-gray-400">Always on</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
    {/* Task #2333 — Warn before renaming a category referenced by transactions */}
    <AlertDialog open={!!renameConfirm} onOpenChange={(open) => { if (!open) setRenameConfirm(null); }}>
      <AlertDialogContent data-testid="dialog-confirm-rename-category">
        <AlertDialogHeader>
          <AlertDialogTitle>Rename this category?</AlertDialogTitle>
          <AlertDialogDescription>
            {renameConfirm ? (
              <>
                This category is used on{" "}
                <span className="font-semibold" data-testid="text-rename-usage-count">
                  {renameConfirm.count} {renameConfirm.count === 1 ? "transaction" : "transactions"}
                </span>
                . Renaming it to{" "}
                <span className="font-semibold">"{renameConfirm.name}"</span>{" "}
                will retroactively re-label every one of those rows in your P&amp;L and history.
              </>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="button-rename-category-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="button-rename-category-confirm"
            onClick={() => {
              if (!renameConfirm) return;
              const { value, source, name } = renameConfirm;
              setRenameConfirm(null);
              performRename(value, source, name);
            }}
          >
            Rename anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </fieldset>
    </>
  );
}

